import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, ListPage } from "@redipos/ui";
import type { ConsoleSubscriptionListRow } from "@redipos/contracts";
import { finanzasApi } from "../api.js";

const STATUS_VARIANT = {
  active: "success",
  trial: "warning",
  past_due: "warning",
  cancelled: "neutral",
  expired: "danger",
};

const STATUS_LABEL = {
  active: "Activo",
  trial: "Prueba",
  past_due: "Pago pendiente",
  cancelled: "Cancelado",
  expired: "Vencido",
};

const BILLING_REASON_LABEL = {
  ok: "Al día",
  payment_grace: "En gracia",
  payment_overdue: "Falta de pago",
  cancelled_grace: "Cancelada en gracia",
  cancelled_overdue: "Cancelación vencida",
  expired: "Vencida",
};

function formatDate(value: unknown) {
  return value ? new Date(value as string | number | Date).toLocaleDateString("es-PY") : "—";
}

export default function SubscriptionsPage() {
  const [rows, setRows] = useState<ConsoleSubscriptionListRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    finanzasApi
      .subscriptions()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  const activeCount = rows.filter((s) => s.status === "active").length;
  const trialCount = rows.filter((s) => s.status === "trial").length;
  const riskCount = rows.filter((s) => s.status === "past_due" || s.status === "expired").length;
  const metrics = [
    { key: "total", label: "Total", value: loading ? "—" : rows.length },
    { key: "active", label: "Activas", value: loading ? "—" : activeCount },
    { key: "trial", label: "Trial", value: loading ? "—" : trialCount },
    { key: "risk", label: "En riesgo", value: loading ? "—" : riskCount },
  ];

  return (
    <ListPage
      title="Suscripciones"
      subtitle="Estado de cada cliente respecto a su plan"
      metrics={metrics}
      sectionTitle="Listado de suscripciones"
    >
        <div className="table-x-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-elevated text-left text-fg-subtle text-xs uppercase tracking-wide">
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium">Período</th>
              <th className="px-4 py-3 font-medium text-right"> </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-fg-subtle">
                  Cargando…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-fg-subtle">
                  Sin suscripciones
                </td>
              </tr>
            ) : (
              rows.map((s) => (
                <tr key={s.id} className="hover:bg-surface-elevated/60 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-fg">{s.tenant?.name}</p>
                    <p className="text-xs text-fg-subtle">{s.tenant?.slug || s.tenant?.id || "—"}</p>
                  </td>
                  <td className="px-4 py-3 text-fg-muted">{s.plan?.name || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge variant={((STATUS_VARIANT as Record<string, string>)[String(s.status)] || "neutral") as any}>
                      {(STATUS_LABEL as Record<string, string>)[String(s.status)] || s.status}
                    </Badge>
                    {s.tenant?.isActive === false && (
                      <span className="ml-2 text-xs text-danger">· cuenta suspendida</span>
                    )}
                    {s.billing?.reason && s.billing.reason !== "ok" && (
                      <p className="mt-1 text-xs text-fg-subtle">
                        {(BILLING_REASON_LABEL as Record<string, string>)[String(s.billing.reason)] || s.billing.reason}
                        {typeof s.billing.graceDaysRemaining === "number"
                          ? ` · ${s.billing.graceDaysRemaining} días de gracia`
                          : ""}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-fg-subtle text-xs whitespace-nowrap">
                    {s.currentPeriodEnd
                      ? `hasta ${new Date(s.currentPeriodEnd).toLocaleDateString("es-PY")}`
                      : "—"}
                    <p>último pago: {formatDate(s.billing?.lastPaymentAt)}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/clientes/${s.tenant?.id}/facturacion`}
                      className="text-brand-500 hover:text-brand-400 text-sm font-medium"
                    >
                      Gestionar
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
    </ListPage>
  );
}
